import {
  OrbitalTrackAttachStatus,
  OrbitalTrackRentalPopupType
} from '@apeiron/orbital'
import { StarLite } from '@apeiron/star'
import {
  AnimatedBox,
  ApeironBlueButton,
  INFO_POPUP_TYPE,
  InfoPopup,
  MayNull,
  useDate
} from '@apeiron/temp'
import { Box, styled, Typography } from '@mui/material'
import SingleRentalView from '@src/components/orbitalTrack/detachRenew/SingleRentalView'
import DetachPlanetButton from '@src/components/orbitalTrack/detail/DetachPlanetButton'
import { SectionContent } from '@src/components/planet/detail/SectionContent'
import ApeironLabelTextfield from '@src/components/share/apeiron/ApeironLabelTextfield'
import ApeironSectionTitle from '@src/components/share/apeiron/ApeironSectionTitle'
import StarCard from '@src/components/stars/cards/StarCard'
import { ROUTE } from '@src/constants/route'
import { useOrbitalTrackRenewDetachContext } from '@src/contexts/orbitalTrack/OrbtialTrackRenewDetachContext'
import { useStarHelper } from '@src/hooks/apeiron/star/useStarHelper'
import useGetOrbitalTracks from '@src/hooks/graphql/useGetOrbitalTracks'
import {
  OrbitalTrackDetachDetail,
  OrbitalTrackLite
} from '@src/types/orbitalTrack'
import { differenceInDays } from 'date-fns'
import * as R from 'ramda'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useAsyncEffect from 'use-async-effect'

const Popup = styled(InfoPopup)`
  height: 95vh;
  max-height: 600px;
  max-width: 800px;
  width: 95vw;
  .Custom-Content {
    margin-top: -47px;
    overflow-y: auto;
  }
`

const StyledAnimatedBox = styled(AnimatedBox)`
  top: 580px;
`

const Container = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 10px;
`

const StarNameText = styled(Typography)`
  font-size: 24px;
  line-height: 28px;
  color: #b1b3b3;
  strong {
    font-weight: 700;
    color: #ffffff;
  }
`

const RowContainer = styled(Box)`
  display: flex;
  gap: 20px;
  justify-content: center;
`

const InfoContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 15px;
`

const StyledStarCard = styled(StarCard)`
  flex: 1;
`

const AgeBuffText = styled(Typography)`
  font-size: 16px;
  line-height: 18px;
  font-weight: 700;
  color: #e71c80;
`

const RemainingText = styled(Typography)`
  font-size: 16px;
  line-height: 18px;
  color: white;
  strong {
    color: #ffbf39;
    font-weight: 700;
  }
`

const EndButton = styled(ApeironBlueButton)`
  border-radius: 4px;
  flex: 1;
  font-size: 16px;
  font-weight: 400;
  padding: unset;
`

const RenewButton = styled(EndButton)`
  --background-color: #ffbf36;
  color: black;
`

const StyledDetachButton = styled(DetachPlanetButton)`
  width: unset;
  padding: 0 40px;
`

const PlanetOrbitalTrackSection = (props: Props) => {
  const { className, attachedPlanetId } = props

  const { fetch: fetchTracks, data: tracks } = useGetOrbitalTracks()

  const { loading, stars, checkAttachStatus, fetchRentalDetachDetail } =
    useOrbitalTrackRenewDetachContext()

  const { dateValid } = useDate()

  const { t } = useTranslation()

  const [detachDetail, setDetachDetail] =
    useState<MayNull<OrbitalTrackDetachDetail>>(null)

  const [isOpened, setIsOpened] = useState(false)

  const [popupType, setPopupType] = useState<OrbitalTrackRentalPopupType>(
    OrbitalTrackRentalPopupType.Renew
  )

  const track = useMemo(
    (): OrbitalTrackLite | null => R.propOr(null, '0', tracks),
    [tracks]
  )

  const star = useMemo(
    (): StarLite | null => R.propOr(null, '0', stars),
    [stars]
  )

  const attachStatus = useMemo((): OrbitalTrackAttachStatus => {
    if (R.isNotNil(star) && R.isNotNil(track)) {
      return checkAttachStatus(loading, star, track, detachDetail)
    }

    return OrbitalTrackAttachStatus.Loading
  }, [star, track, loading, detachDetail, checkAttachStatus])

  const { isOwner: isStarOwner } = useStarHelper(star)

  const handleOnSuccess = useCallback(
    (successIds: string[]) => {
      if (!R.isEmpty(successIds)) {
        setIsOpened(false)
        fetchTracks({
          filterInput: { attachedPlanetId }
        })
      }
    },
    [attachedPlanetId, fetchTracks]
  )

  const handleOnClose = useCallback(() => {
    setIsOpened(false)
  }, [])

  const handleOnRenew = useCallback(() => {
    setPopupType(OrbitalTrackRentalPopupType.Renew)
    setIsOpened(true)
  }, [])

  const handleOnEnd = useCallback(() => {
    setPopupType(OrbitalTrackRentalPopupType.Planet_Owner)
    setIsOpened(true)
  }, [])

  const handleOnRenderURL = useCallback((star: StarLite) => {
    return `${ROUTE.MARKETPLACE_STAR_DETAIL}?starId=${star.id}`
  }, [])

  useEffect(() => {
    fetchTracks({
      filterInput: { attachedPlanetId }
    })
  }, [attachedPlanetId, fetchTracks])

  useAsyncEffect(
    async (isActive: () => boolean) => {
      if (R.isNotNil(track)) {
        const parameter = R.assoc(
          'isOwnerAttaching',
          isStarOwner,
          track
        ) as OrbitalTrackLite

        const detachList = await fetchRentalDetachDetail([parameter])

        if (!isActive()) return

        setDetachDetail(R.pathOr(undefined, ['0', 'detachDetail'], detachList))
      }
    },
    [track?.id]
  )

  const showSection =
    !R.isNil(track) &&
    !R.isNil(star) &&
    attachStatus !== OrbitalTrackAttachStatus.Loading &&
    attachStatus !== OrbitalTrackAttachStatus.NoAttach

  const ButtonComponent = useMemo(() => {
    if (showSection) {
      switch (attachStatus) {
        case OrbitalTrackAttachStatus.SelfAttach:
        case OrbitalTrackAttachStatus.TransferredAttach:
          return (
            <StyledDetachButton
              track={track}
              onSuccess={(trackId: string) => handleOnSuccess([trackId])}
            />
          )

        case OrbitalTrackAttachStatus.RentAttach:
          return (
            <>
              <RenewButton disabled={!track?.renewable} onClick={handleOnRenew}>
                {t('common.button.renew')}
              </RenewButton>
              <EndButton onClick={handleOnEnd}>
                {t('common.button.end')}
              </EndButton>
            </>
          )
      }
    }

    return <></>
  }, [
    attachStatus,
    handleOnEnd,
    handleOnRenew,
    handleOnSuccess,
    showSection,
    t,
    track
  ])

  return showSection ? (
    <StyledAnimatedBox>
      <ApeironSectionTitle title={t('planet.detail.orbital_track.title')} />
      <SectionContent className={className}>
        <Container>
          <StarNameText>
            {`#${star.id} `}
            <strong>{`T${star.tier.level + 1} ${star.name}`}</strong>
          </StarNameText>
          <RowContainer>
            <StyledStarCard
              noFooter
              noInfo
              showExplain={false}
              star={star}
              onRenderURL={handleOnRenderURL}
            />
            <InfoContainer>
              {dateValid(track.rentalEndTime) && (
                <ApeironLabelTextfield
                  explain={t('planet.detail.orbital_track.remaining_info')}
                  text={
                    <RemainingText>
                      <strong>
                        {Math.max(
                          differenceInDays(
                            new Date(track.rentalEndTime),
                            new Date()
                          ),
                          0
                        )}
                      </strong>{' '}
                      {t('common.days')}
                    </RemainingText>
                  }
                  title={t('planet.detail.orbital_track.remaining')}
                />
              )}
              <ApeironLabelTextfield
                explain={t('planet.detail.orbital_track.aging_info')}
                text={<AgeBuffText>x{track.totalAgingBuff}</AgeBuffText>}
                title={t('godiverse.buff.aging')}
              />

              <ApeironLabelTextfield
                explain={t('planet.detail.orbital_track.track_info')}
                text={track.totalTrackCount}
                title={t('godiverse.buff.orbital_track')}
              />
            </InfoContainer>
          </RowContainer>
          <RowContainer>{ButtonComponent}</RowContainer>
          {attachStatus === OrbitalTrackAttachStatus.RentAttach && (
            <Popup
              type={INFO_POPUP_TYPE.BLACK}
              keepMounted={false}
              open={isOpened}
              onClose={handleOnClose}
            >
              <SingleRentalView
                popupType={popupType}
                star={star}
                planet={detachDetail?.detachPlanet || null}
                onSuccess={handleOnSuccess}
                rentalDetail={detachDetail}
                tracks={[track]}
              />
            </Popup>
          )}
        </Container>
      </SectionContent>
    </StyledAnimatedBox>
  ) : (
    <></>
  )
}

type Props = {
  className?: string
  attachedPlanetId: string
}

export default PlanetOrbitalTrackSection
